import PropTypes from 'prop-types';
import { includes, find, propEq, pick } from 'ramda';
import { formatMoneyWithSign } from 'utils/currencyHelper';
import Presenter from 'utils/PropTypesPresenter';
import BidRequestPresenter from 'presenters/BidRequestPresenter';

export const PURPOSE_TYPES = {
  refinance: ['refinance_no_cash_out', 'refinance_cash_out', 'refinance'],
  purchase: ['purchase'],
};

export const PURPOSE_OPTIONS = [
  { value: 'refinance_no_cash_out', label: 'Refinance - No Cash-out' },
  { value: 'refinance_cash_out', label: 'Refinance - Cash-out' },
];

export const CREDIT_SCORE_OPTIONS = [
  { value: 780, label: '700+ Excellent' },
  { value: 760, label: '760-850 Excellent' },
  { value: 740, label: '740-759 Very Good' },
  { value: 720, label: '720-739 Very Good' },
  { value: 710, label: '680-739 Very Good' },
  { value: 700, label: '700-719 Good' },
  { value: 680, label: '680-699 Good' },
  { value: 660, label: '660-679 Fair' },
  { value: 660, label: '660-679 Fair' },
  { value: 650, label: '620-699 Good' },
  { value: 640, label: '640-659 Fair' },
  { value: 620, label: '620-639 Poor' },
  { value: 300, label: '300-619 Poor' },
  { value: 550, label: '300-579 Poor' },
  { value: 600, label: '580-619 Fair' },
];

export const PROPERTY_TYPE_OPTIONS = [
  { value: 'single_family_home', label: 'Single Family Home' },
  { value: 'town_home', label: 'Townhome' },
  { value: 'condominium', label: 'Condominium' },
  { value: 'multi_family', label: 'Multi-Family' },
  { value: 'manufactured', label: 'Manufactured' },
];

export const OCCUPANCY_OPTIONS = [
  { value: 'primary', label: 'Primary Home' },
  { value: 'second_home', label: 'Second Home' },
  { value: 'investment_property', label: 'Investment Property' },
];

export const STAGE_OPTIONS = [{ value: 'approved', label: 'Yes' }, { value: 'pre_qualified', label: 'No' }];

export const buildSurveyValues = bidRequest => ({
  priceTarget: BidRequestPresenter.getPriceTarget(bidRequest),
  downPaymentTarget: BidRequestPresenter.getDownPaymentTarget(bidRequest),
  purpose: BidRequestPresenter.purpose(bidRequest),
  incomeAnnualPreTax: BidRequestPresenter.getIncomeAnnualPreTax(bidRequest),
  mortgageTermYears: BidRequestPresenter.mortgageTermYears(bidRequest),
  creditScore: BidRequestPresenter.creditScore(bidRequest),
  monthlyDebt: BidRequestPresenter.getMonthlyDebt(bidRequest),
  dtiEstimate: BidRequestPresenter.getDtiEstimate(bidRequest),
  propertyType: BidRequestPresenter.propertyType(bidRequest),
  cashOutAmount: BidRequestPresenter.getCashOutAmount(bidRequest),
  occupancy: BidRequestPresenter.occupancy(bidRequest),
  stage: BidRequestPresenter.stage(bidRequest),
  hadBankrupt: BidRequestPresenter.hadBankrupt(bidRequest),
  hadForeclosure: BidRequestPresenter.hadForeclosure(bidRequest),
  hadMilitaryService: BidRequestPresenter.hadMilitaryService(bidRequest),
  firstTime: BidRequestPresenter.firstTime(bidRequest),
  reasonMessage: BidRequestPresenter.reasonMessage(bidRequest),
  state: BidRequestPresenter.state(bidRequest),
  city: BidRequestPresenter.city(bidRequest),
  mortgageBalance:
    BidRequestPresenter.getPriceTarget(bidRequest) - BidRequestPresenter.getDownPaymentTarget(bidRequest),
  locationString: [BidRequestPresenter.city(bidRequest), BidRequestPresenter.state(bidRequest)].join(', '),
});

export const BID_REQUEST_KEYS = [
  'priceTarget',
  'downPaymentTarget',
  'purpose',
  'incomeAnnualPreTax',
  'mortgageTermYears',
  'creditScore',
  'monthlyDebt',
  'dtiEstimate',
  'propertyType',
  'cashOutAmount',
  'occupancy',
  'stage',
  'hadBankrupt',
  'hadForeclosure',
  'hadMilitaryService',
  'firstTime',
  'reasonMessage',
  'state',
  'city',
];

export default new Presenter(
  {
    priceTarget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    downPaymentTarget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    purpose: PropTypes.string,
    incomeAnnualPreTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mortgageTermYears: PropTypes.number,
    creditScore: PropTypes.number,
    monthlyDebt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dtiEstimate: PropTypes.number,
    propertyType: PropTypes.string,
    cashOutAmount: PropTypes.number,
    occupancy: PropTypes.string,
    stage: PropTypes.string,
    hadBankrupt: PropTypes.bool,
    hadForeclosure: PropTypes.bool,
    hadMilitaryService: PropTypes.bool,
    firstTime: PropTypes.bool,
    reasonMessage: PropTypes.string,
    state: PropTypes.string,
    city: PropTypes.string,
    locationString: PropTypes.string,
    mortgageBalance: PropTypes.number,
  },
  {
    isRefinanceFlow(surveyValues) {
      return includes(this.purpose(surveyValues), PURPOSE_TYPES.refinance);
    },
    readablePriceTarget(surveyValues) {
      const priceTarget = Number(this.priceTarget(surveyValues));
      return formatMoneyWithSign(priceTarget, 'USD');
    },
    readableDownPaymentTarget(surveyValues) {
      const downPaymentTarget = Number(this.downPaymentTarget(surveyValues));
      return formatMoneyWithSign(downPaymentTarget, 'USD');
    },
    redableIncomeAnnualPreTax(surveyValues) {
      const incomeAnnualPreTax = Number(this.incomeAnnualPreTax(surveyValues));
      return formatMoneyWithSign(incomeAnnualPreTax, 'USD');
    },
    readableCreditScore(surveyValues) {
      const creditScore = Number(this.creditScore(surveyValues));
      const { label } = find(propEq('value', creditScore), CREDIT_SCORE_OPTIONS);
      return label;
    },
    readableMonthlyDebt(surveyValues) {
      const monthlyDebt = Number(this.monthlyDebt(surveyValues));
      return formatMoneyWithSign(monthlyDebt, 'USD');
    },
    readableMortgageBalance(surveyValues) {
      const mortgageBalance = Number(this.mortgageBalance(surveyValues));
      return formatMoneyWithSign(mortgageBalance, 'USD');
    },
    readableCashOutAmount(surveyValues) {
      const cashOutAmount = Number(this.cashOutAmount(surveyValues));
      return formatMoneyWithSign(cashOutAmount, 'USD');
    },
    redablePropertyType(surveyValues) {
      const propertyType = this.propertyType(surveyValues);
      const { label } = find(propEq('value', propertyType), PROPERTY_TYPE_OPTIONS);
      return label;
    },
    readableOccupancy(surveyValues) {
      const occupancy = this.occupancy(surveyValues);
      const { label } = find(propEq('value', occupancy), OCCUPANCY_OPTIONS);
      return label;
    },
    readablePurpose(surveyValues) {
      const purpose = this.purpose(surveyValues);
      const { label } = find(propEq('value', purpose), PURPOSE_OPTIONS);
      return label;
    },
    readableStage(surveyValues) {
      const stage = this.stage(surveyValues);
      const { label } = find(propEq('value', stage), STAGE_OPTIONS);
      return label;
    },
    readableMortgageTermYears(surveyValues) {
      return `${this.mortgageTermYears(surveyValues)} Years`;
    },
    humanHadBankrupt(surveyValues) {
      const hadBankrupt = this.hadBankrupt(surveyValues);
      return hadBankrupt ? 'Yes' : 'No';
    },
    humanHadForeclosure(surveyValues) {
      const hadForeclosure = this.hadForeclosure(surveyValues);
      return hadForeclosure ? 'Yes' : 'No';
    },
    humanHadMilitaryService(surveyValues) {
      const hadMilitaryService = this.hadMilitaryService(surveyValues);
      return hadMilitaryService ? 'Yes' : 'No';
    },
    humanFirstTime(surveyValues) {
      const firstTime = this.firstTime(surveyValues);
      return firstTime ? 'Yes' : 'No';
    },
    calculateDti(surveyValues) {
      const remainingPayment = (this.priceTarget(surveyValues) - this.downPaymentTarget(surveyValues)) * 0.0556;
      const pricePercent = this.priceTarget(surveyValues) * 0.017;
      const annualDebt = this.monthlyDebt(surveyValues) * 12;
      return ((remainingPayment + pricePercent + annualDebt) / this.incomeAnnualPreTax(surveyValues)) * 100;
    },
    buildBidRequestAtrributes(surveyValues) {
      const attributes = pick(BID_REQUEST_KEYS, surveyValues);
      attributes.dtiEstimate = this.calculateDti(surveyValues);
      return { ...attributes, type: 'favorite_los_only' };
    },
    isCashOut(purpose) {
      return purpose === 'refinance_cash_out';
    },
    buildInitialSurveyFlowValues(surveyValues, flowType) {
      const isRefinanceFlow = includes(flowType, PURPOSE_TYPES.refinance);

      return {
        ...surveyValues,
        purpose: flowType,
        stage: isRefinanceFlow ? 'refinance' : null,
      };
    },

    calculateConversionValue(surveyValues) {
      const valueRatio = 0.015;
      const fixedExpense = 1500;
      const baseValue = (this.priceTarget(surveyValues) - this.downPaymentTarget(surveyValues)) * valueRatio;
      const cappedBaseValue = Math.min(baseValue, 6500);
      const netCappedBaseValue = cappedBaseValue - fixedExpense;
      let conversionRateFator;
      if (this.stage(surveyValues) === 'approved') {
        if (this.creditScore(surveyValues) < 620) {
          conversionRateFator = 0.25;
        } else if (this.creditScore(surveyValues) < 680) {
          conversionRateFator = 1.5;
        } else {
          conversionRateFator = 1.75;
        }
      } else if (this.creditScore(surveyValues) < 620) {
        conversionRateFator = 0.25;
      } else if (this.creditScore(surveyValues) < 680) {
        conversionRateFator = 1;
      } else {
        conversionRateFator = 1.25;
      }

      const conversionRate = 0.1 * conversionRateFator;
      const conversionValue = netCappedBaseValue * conversionRate;

      return Math.round(conversionValue);
    },
  },
);



// WEBPACK FOOTER //
// ./src/presenters/MortgageSurveyPresenter.js